import { useMutation, useQuery } from "@apollo/client/react"
import { Calendar, LayoutGrid, LogOut, LucideIcon, Menu, Star } from "lucide-react"
import React, { useState } from "react"
import { NavLink, Outlet, ScrollRestoration, useLocation } from "react-router-dom"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { useLogout } from "~/auth/use-logout"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { cn } from "~/common/cn"
import { dashboardPath, internalAdminDashboardPath, rootPath, watchlistPath } from "~/common/paths"
import { Sheet, SheetContent, SheetTitle } from "~/ui/sheet"

const NAV_USER_SETTINGS_QUERY = gql(/* GraphQL */ `
  query NavUserSettings {
    viewer {
      id
      digestPreference
      userLocations {
        id
        position
        cityZip
        radius
      }
    }
  }
`)

const NAV_ACCOUNT_SETTINGS_UPDATE = gql(/* GraphQL */ `
  mutation NavAccountSettingsUpdate($input: AccountSettingsUpdateInput!) {
    accountSettingsUpdate(input: $input) {
      user {
        id
        digestPreference
        userLocations {
          id
          position
          cityZip
          radius
        }
      }
    }
  }
`)

const RADIUS_OPTIONS = [
  { value: "everywhere", label: "Everywhere" },
  { value: "5mi", label: "5 mi" },
  { value: "10mi", label: "10 mi" },
  { value: "15mi", label: "15 mi" },
  { value: "25mi", label: "25 mi" },
  { value: "50mi", label: "50 mi" },
] as const

type LocationState = { cityZip: string; radius: string }

function buildInitialLocation(
  userLocations: Array<{ position: number; cityZip: string; radius: string }>
): LocationState {
  const loc = userLocations.find((l) => l.position === 1)
  return loc ? { cityZip: loc.cityZip, radius: loc.radius } : { cityZip: "", radius: "everywhere" }
}

const LocationForm: React.FC<{
  userLocations: Array<{ position: number; cityZip: string; radius: string }>
  onSave: (loc: LocationState) => void
  saving: boolean
}> = ({ userLocations, onSave, saving }) => {
  const [location, setLocation] = useState<LocationState>(() => buildInitialLocation(userLocations))
  return (
    <div className="px-4 pb-3 pt-1 space-y-1.5">
      <div className="flex gap-1">
        <input
          type="text"
          placeholder="City or zip"
          value={location.cityZip}
          onChange={(e) => setLocation({ ...location, cityZip: e.target.value })}
          className="flex-1 min-w-0 text-[11px] border border-stone-200 bg-stone-50 px-2 py-1 outline-none focus:border-stone-400 placeholder:text-stone-300"
        />
        <select
          value={location.radius}
          onChange={(e) => setLocation({ ...location, radius: e.target.value })}
          className="text-[11px] border border-stone-200 bg-stone-50 px-1 py-1 outline-none focus:border-stone-400"
        >
          {RADIUS_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <button
        onClick={() => onSave(location)}
        disabled={saving}
        className="w-full text-[11px] bg-stone-900 text-stone-50 py-1.5 hover:bg-stone-700 transition-colors disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save location"}
      </button>
    </div>
  )
}

type NavItem = {
  label: string
  to: string
  icon: LucideIcon
  emoji?: string
  disabled?: boolean
  comingSoon?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", to: dashboardPath({}), icon: LayoutGrid, emoji: "🕹️" },
  { label: "Watchlist", to: watchlistPath({}), icon: Star, emoji: "🔍" },
  {
    label: "Calendar",
    to: "#calendar",
    icon: Calendar,
    disabled: true,
    comingSoon: true,
    emoji: "📅",
  },
]

const NavItem = ({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) => {
  const Icon = item.icon
  const iconEl = item.emoji ? (
    <span className="text-sm leading-none">{item.emoji}</span>
  ) : (
    <Icon size={14} />
  )
  if (item.disabled) {
    return (
      <li>
        <span className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-stone-300 cursor-default select-none">
          {iconEl}
          {item.label}
          {item.comingSoon && (
            <span className="text-[9px] font-semibold tracking-wide text-stone-300 ml-1">
              Coming Soon
            </span>
          )}
        </span>
      </li>
    )
  }
  return (
    <li>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors",
            isActive
              ? "bg-stone-900 text-stone-50"
              : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
          )
        }
      >
        {iconEl}
        {item.label}
      </NavLink>
    </li>
  )
}

const NavList = ({
  onNavigate,
  showNavLabel = true,
  onOpenRadarSettings,
}: {
  onNavigate?: () => void
  showNavLabel?: boolean
  onOpenRadarSettings?: () => void
}) => {
  const logout = useLogout()
  const { uiAccess } = useUiAccess()
  const { viewer } = useViewerMaybe()
  const { data: settingsData } = useQuery(NAV_USER_SETTINGS_QUERY)
  const [updateSettings, { loading: saving }] = useMutation(NAV_ACCOUNT_SETTINGS_UPDATE)
  const [locationOpen, setLocationOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)

  const digestPreference = settingsData?.viewer?.digestPreference ?? "OFF"
  const digestOn = digestPreference !== "OFF"

  const handleDigestToggle = async () => {
    const next = digestOn ? "OFF" : "DAILY"
    await updateSettings({
      variables: { input: { digestPreference: next as "DAILY" | "OFF" } },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleLocationSave = async (loc: LocationState) => {
    const locationsInput =
      loc.cityZip.trim().length > 0
        ? [{ position: 1, cityZip: loc.cityZip, radius: loc.radius }]
        : []
    await updateSettings({
      variables: { input: { locations: locationsInput } },
      refetchQueries: ["Listings"],
      onError: (err) => toast.error(err.message),
      onCompleted: () => toast.success("Location saved"),
    })
    setLocationOpen(false)
  }

  const savedLocation =
    (settingsData?.viewer?.userLocations ?? [])
      .filter((l) => l.cityZip && l.radius !== "everywhere")
      .map((l) => {
        const radiusLabel = RADIUS_OPTIONS.find((r) => r.value === l.radius)?.label ?? l.radius
        return `${l.cityZip} · ${radiusLabel}`
      })
      .at(0) ?? null

  return (
    <>
      <nav className="flex-1 py-4 px-2 overflow-y-auto">
        {showNavLabel && (
          <p className="px-2 mb-2 text-[9px] font-semibold tracking-[3px] uppercase text-stone-400">
            Navigation
          </p>
        )}
        <ul className="space-y-0.5">
          {NAV_ITEMS.slice(0, 2).map((item) => (
            <NavItem key={item.label} item={item} onNavigate={onNavigate} />
          ))}
          {onOpenRadarSettings && (
            <li>
              <button
                onClick={onOpenRadarSettings}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-[13px] text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
              >
                <span className="text-sm leading-none">ᯤ</span>
                Search Settings
              </button>
            </li>
          )}
          {NAV_ITEMS.slice(2).map((item) => (
            <NavItem key={item.label} item={item} onNavigate={onNavigate} />
          ))}
          {uiAccess?.canInternalAdmin.value && (
            <li>
              <NavLink
                to={internalAdminDashboardPath({})}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors",
                    isActive
                      ? "bg-stone-900 text-stone-50"
                      : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  )
                }
              >
                <LayoutGrid size={14} />
                Admin
              </NavLink>
            </li>
          )}
        </ul>
      </nav>

      <div className="border-t border-stone-200 text-[13px]">
        {/* 📍 Location */}
        <button
          onClick={() => setLocationOpen((v) => !v)}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
        >
          <span className="text-sm leading-none">📍</span>
          Location
          {savedLocation && (
            <span className="ml-auto text-[10px] text-stone-400 truncate max-w-[100px]">
              {savedLocation}
            </span>
          )}
        </button>
        {locationOpen && settingsData?.viewer && (
          <LocationForm
            userLocations={settingsData.viewer.userLocations}
            onSave={handleLocationSave}
            saving={saving}
          />
        )}

        {/* 📬 Email Digest */}
        <button
          onClick={handleDigestToggle}
          disabled={saving}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors disabled:opacity-50"
        >
          <span className="text-sm leading-none">📬</span>
          Email Digest
          <span className="ml-auto text-[11px] text-stone-400">{digestOn ? "On" : "Off"}</span>
        </button>

        {/* Account */}
        <button
          onClick={() => setAccountOpen((v) => !v)}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
        >
          {viewer?.avatarThumbUrl ? (
            <img
              src={viewer.avatarThumbUrl}
              alt={viewer.firstName ?? ""}
              className="h-5 w-5 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="h-5 w-5 rounded-full bg-stone-900 flex items-center justify-center text-[9px] font-bold text-stone-50 shrink-0">
              {viewer?.firstName?.[0]?.toUpperCase() ?? "U"}
            </div>
          )}
          <span className="truncate">{viewer?.firstName}</span>
        </button>
        {accountOpen && (
          <div className="px-4 pb-3 text-[12px] text-stone-500">You are in beta mode</div>
        )}

        {/* Sign Out */}
        <button
          onClick={() => {
            logout()
            onNavigate?.()
          }}
          className="flex w-full items-center gap-3 px-4 py-3 text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition-colors"
        >
          <LogOut size={14} />
          Sign Out
        </button>
      </div>
    </>
  )
}

export const SidebarLayout = () => {
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()
  const isOnDashboard = location.pathname === dashboardPath({})

  return (
    <div className="flex w-full min-h-screen">
      {/* Desktop sidebar */}
      <div className="hidden md:flex w-[220px] shrink-0 flex-col bg-stone-50 border-r border-stone-200 h-screen sticky top-0 overflow-hidden">
        <div className="px-5 pt-6 pb-5 border-b border-stone-200">
          <span
            className="text-sm font-bold tracking-[0.15em] uppercase text-stone-900"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            SCOUT RADAR
          </span>
        </div>
        <NavList />
      </div>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-10 flex items-center px-4 py-3 bg-white border-b border-stone-200">
        <NavLink
          to={rootPath({})}
          className="flex-1 text-base font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          SCOUT RADAR
        </NavLink>
        <button
          onClick={() => setNavOpen(true)}
          className="ml-auto text-stone-600 hover:text-stone-900 transition-colors"
          aria-label="Open navigation"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Mobile nav drawer */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="p-0 flex flex-col w-[280px]">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="px-5 pt-6 pb-5 border-b border-stone-200">
            <span
              className="text-sm font-bold tracking-[0.15em] uppercase text-stone-900"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              SCOUT RADAR
            </span>
          </div>
          <NavList
            onNavigate={() => setNavOpen(false)}
            showNavLabel={false}
            onOpenRadarSettings={
              isOnDashboard
                ? () => {
                    window.dispatchEvent(new CustomEvent("open-radar-settings"))
                    setNavOpen(false)
                  }
                : undefined
            }
          />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden md:mt-0 mt-[49px]">
        {/* Top bar */}
        {/* HIDDEN: search bar disabled pending redesign
        <div className="hidden md:flex h-11 shrink-0 items-center border-b border-stone-200 bg-white px-5">
          <div className="flex items-center gap-2 rounded border border-stone-200 bg-stone-50 px-3 py-1.5 max-w-xs">
            <Search size={12} className="text-stone-400 shrink-0" />
            <input
              type="text"
              placeholder="Keywords, maker, style…"
              value={searchQuery}
              onChange={(e) => {
                if (e.target.value) {
                  setSearchParams({ q: e.target.value })
                } else {
                  setSearchParams({})
                }
              }}
              className="flex-1 bg-transparent text-[12px] text-stone-700 placeholder:text-stone-400 outline-none"
            />
          </div>
        </div>
        */}

        <Outlet />
        <ScrollRestoration />
      </div>
      <a
        href={`mailto:feedback@scoutradar.app?subject=Scout%20Radar%20Bug%20Report&body=Page%3A%20${encodeURIComponent(window.location.href)}`}
        className="fixed bottom-5 right-5 z-50 flex items-center justify-center w-9 h-9 bg-white border border-stone-200 shadow-md hover:shadow-lg transition-shadow rounded-full text-base"
        aria-label="Report a bug"
        title="Report a bug"
      >
        🐛
      </a>
    </div>
  )
}
